import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";

const cardStyle = {
  background: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  fontWeight: 700,
  color: "#334155",
  fontSize: "15px",
  marginBottom: "14px",
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  backgroundColor: "#fff",
  color: "#334155",
  fontSize: "14px",
};

const accentColor = "#06d1c6";

function formatINR(num) {
  const s = String(Math.round(num));
  const len = s.length;
  if (len <= 3) return s;
  let result = s.slice(-3);
  for (let i = len - 3; i > 0; i -= 2) {
    const start = Math.max(0, i - 2);
    result = s.slice(start, i) + "," + result;
  }
  return result;
}

const SAMPLE_SCHEMES = [
  { id: 1, scheme: "Parag Parikh Flexi Cap Fund Reg (G)", managedAum: 407552, investedClients: 15 },
  { id: 2, scheme: "Nippon India Multi-Asset Allocation Fund Reg (G)", managedAum: 347495, investedClients: 5 },
  { id: 3, scheme: "Nippon India Multi Cap Fund (G)", managedAum: 329034, investedClients: 11 },
  { id: 4, scheme: "Motilal Oswal Gold and Silver Passive FoF (G)", managedAum: 194744, investedClients: 5 },
  { id: 5, scheme: "Franklin India Opportunities Fund (G)", managedAum: 149819, investedClients: 3 },
  { id: 6, scheme: "DSP Large Cap Fund Reg (G)", managedAum: 126685, investedClients: 1 },
  { id: 7, scheme: "DSP Large & Mid Cap Fund Reg (G)", managedAum: 118200, investedClients: 4 },
  { id: 8, scheme: "Parag Parikh Conservative Hybrid Fund Reg (G)", managedAum: 95000, investedClients: 2 },
  { id: 9, scheme: "DSP Ultra Short Fund Reg Plan (G)", managedAum: 87500, investedClients: 6 },
  { id: 10, scheme: "SBI Multi Asset Allocation Fund Reg (G)", managedAum: 72000, investedClients: 3 },
];

const FILTERS = [
  { key: "buy", label: "Buy", count: 0, icon: "fa-check", color: "#10b981" },
  { key: "sell", label: "Sell", count: 0, icon: "fa-ellipsis-h", color: "#ef4444" },
  { key: "hold", label: "Hold", count: 0, icon: "fa-pause", color: "#3b82f6" },
  { key: "all", label: "All", count: null, icon: "fa-th", color: "#64748b" },
];

const REMARKS_MAX = 250;

export default function Recommendations() {
  const [searchScheme, setSearchScheme] = useState("");
  const [nfoOnly, setNfoOnly] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("managedAum");
  const [sortDir, setSortDir] = useState("desc");
  const fileInputRef = React.useRef(null);

  // Add Recommendation modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalSchemeId, setModalSchemeId] = useState("");
  const [modalRecommendation, setModalRecommendation] = useState(""); // "buy" | "sell" | "hold"
  const [modalRemarks, setModalRemarks] = useState("");

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const filteredSchemes = SAMPLE_SCHEMES.filter((row) =>
    row.scheme.toLowerCase().includes(searchScheme.toLowerCase())
  );

  const openAddModal = () => {
    setModalSchemeId("");
    setModalRecommendation("");
    setModalRemarks("");
    setShowAddModal(true);
  };

  const closeAddModal = () => setShowAddModal(false);

  const handleAddRecommendationSave = (e) => {
    e.preventDefault();
    // TODO: API submit { modalSchemeId, modalRecommendation, modalRemarks }
    console.log({ schemeId: modalSchemeId, recommendation: modalRecommendation, remarks: modalRemarks });
    closeAddModal();
  };

  const remarksLeft = Math.max(0, REMARKS_MAX - modalRemarks.length);

  useEffect(() => {
    if (!showAddModal) return;
    const onEscape = (e) => {
      if (e.key === "Escape") closeAddModal();
    };
    document.addEventListener("keydown", onEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEscape);
      document.body.style.overflow = "";
    };
  }, [showAddModal]);

  const SortIcon = ({ columnKey }) => (
    <button
      type="button"
      className="btn btn-link p-0 ms-1 border-0"
      style={{ color: sortBy === columnKey ? accentColor : "#94a3b8", fontSize: "12px" }}
      onClick={() => toggleSort(columnKey)}
      aria-label={`Sort by ${columnKey}`}
    >
      <i
        className={`fa fa-sort-${sortBy === columnKey ? (sortDir === "asc" ? "up" : "down") : "up"}`}
        style={{ opacity: sortBy === columnKey ? 1 : 0.4 }}
      />
    </button>
  );

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          {/* Breadcrumb and Header */}
          <div className="mb-4" style={cardStyle}>
            <div className="content-page-header content-page-headersplit">
              <h5>Recommendations</h5>
            </div>
            <nav
              aria-label="breadcrumb"
              style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/dashboard">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <NavLink to="/admin/portfolio/comprehensive">Portfolio Management</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Recommendations
                </li>
              </ol>
            </nav>
          </div>

          {/* Controls */}
          <div style={cardStyle}>
            {/* Row 1: Search + Primary action */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
              <div className="position-relative flex-grow-1" style={{ maxWidth: 420 }}>
                <i
                  className="fa fa-search position-absolute"
                  style={{
                    left: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    fontSize: "14px",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search Scheme"
                  value={searchScheme}
                  onChange={(e) => setSearchScheme(e.target.value)}
                  style={{
                    ...inputStyle,
                    paddingLeft: 40,
                    borderColor: "#e2e8f0",
                  }}
                />
              </div>
              <button
                type="button"
                onClick={openAddModal}
                className="d-flex align-items-center gap-2"
                style={{
                  background: "#10b981",
                  color: "#fff",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                }}
              >
                <i className="fa fa-plus" style={{ fontSize: "12px" }} />
                Add Recommendation
              </button>
            </div>

            {/* Row 2: NFOs toggle + Upload + XLS */}
            <div
              className="d-flex flex-wrap align-items-center gap-3 py-3 px-3 rounded-2"
              style={{ background: "#f8fafc" }}
            >
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: "14px", fontWeight: 500, color: "#475569" }}>
                  NFOs Only
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={nfoOnly}
                  onClick={() => setNfoOnly(!nfoOnly)}
                  style={{
                    width: 40,
                    height: 22,
                    borderRadius: 11,
                    border: "none",
                    background: nfoOnly ? "#10b981" : "#cbd5e1",
                    cursor: "pointer",
                    position: "relative",
                    padding: 0,
                    transition: "background 0.2s ease",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: 2,
                      left: nfoOnly ? 20 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "#fff",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      transition: "left 0.2s ease",
                    }}
                  />
                </button>
                <span style={{ fontSize: "13px", color: "#64748b", fontWeight: 500 }}>
                  {nfoOnly ? "Yes" : "No"}
                </span>
              </div>
              <span
                style={{
                  width: 1,
                  height: 20,
                  background: "#e2e8f0",
                  display: "inline-block",
                }}
              />
              <button
                type="button"
                className="d-flex align-items-center gap-2 btn btn-sm"
                style={{
                  color: "#475569",
                  fontSize: "14px",
                  fontWeight: 500,
                  background: "transparent",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  padding: "6px 12px",
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fa fa-upload" style={{ fontSize: "13px" }} />
                Upload
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx,.csv"
                style={{ display: "none" }}
                onChange={() => {}}
              />
              <button
                type="button"
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 44,
                  height: 36,
                  background: "#10b981",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                title="Download XLS"
              >
                XLS
              </button>
            </div>

            {/* Filter tabs */}
            <div
              className="d-flex flex-wrap gap-1 mt-3 pt-3"
              style={{ borderTop: "1px solid #e2e8f0" }}
              role="tablist"
              aria-label="Recommendation filters"
            >
              {FILTERS.map(({ key, label, count, icon, color }) => {
                const isActive = activeFilter === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveFilter(key)}
                    className="d-flex align-items-center gap-2 border-0 rounded-2"
                    style={{
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? (key === "all" ? "#334155" : "#fff") : "#64748b",
                      background: isActive
                        ? key === "all"
                          ? "#e2e8f0"
                          : color
                        : "transparent",
                      cursor: "pointer",
                      transition: "background 0.15s ease, color 0.15s ease",
                    }}
                  >
                    <i
                      className={`fa ${icon}`}
                      style={{
                        fontSize: "12px",
                        color: isActive && key !== "all" ? "#fff" : color,
                      }}
                    />
                    <span>{label}</span>
                    {count !== null && (
                      <span
                        style={{
                          fontSize: "12px",
                          opacity: isActive ? 1 : 0.8,
                          marginLeft: 2,
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table */}
          <div style={{ ...cardStyle, overflowX: "auto", padding: 0 }}>
            <table className="table table-bordered mb-0" style={{ fontSize: "14px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc" }}>
                  <th style={{ padding: "12px", fontWeight: 600, color: "#334155" }}>Scheme</th>
                  <th className="text-end" style={{ padding: "12px", fontWeight: 600, color: accentColor, borderBottom: `2px solid ${accentColor}` }}>
                    Managed AUM
                    <SortIcon columnKey="managedAum" />
                  </th>
                  <th className="text-end" style={{ padding: "12px", fontWeight: 600, color: "#334155" }}>
                    Invested Clients
                    <SortIcon columnKey="investedClients" />
                  </th>
                  <th style={{ padding: "12px", fontWeight: 600, color: "#334155" }}>
                    <span className="me-1">Recommendation</span>
                    <i className="fa fa-search" style={{ fontSize: "12px", color: "#64748b" }} />
                  </th>
                  <th style={{ padding: "12px", fontWeight: 600, color: "#334155" }}>Last Updated</th>
                  <th style={{ padding: "12px", fontWeight: 600, color: "#334155" }}>Remarks</th>
                  <th style={{ padding: "12px", fontWeight: 600, color: "#334155", width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchemes.map((row) => (
                  <tr key={row.id}>
                    <td style={{ padding: "12px" }}>
                      <NavLink
                        to="#"
                        style={{ color: "#06b6d4", textDecoration: "none", fontWeight: 500 }}
                      >
                        {row.scheme}
                      </NavLink>
                    </td>
                    <td className="text-end" style={{ padding: "12px" }}>
                      {formatINR(row.managedAum)}
                    </td>
                    <td className="text-end" style={{ padding: "12px" }}>
                      {row.investedClients}
                    </td>
                    <td style={{ padding: "12px", color: "#94a3b8" }}>-</td>
                    <td style={{ padding: "12px", color: "#94a3b8" }}>-</td>
                    <td style={{ padding: "12px", color: "#94a3b8" }}>-</td>
                    <td style={{ padding: "12px" }}>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-secondary"
                        aria-label="Actions"
                        title="Actions"
                      >
                        <i className="fa fa-ellipsis-v" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add Recommendation Modal */}
          {showAddModal && (
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-recommendation-title"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
                background: "rgba(0,0,0,0.4)",
              }}
              onClick={(e) => e.target === e.currentTarget && closeAddModal()}
            >
              <div
                style={{
                  background: "#fff",
                  borderRadius: "10px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                  maxWidth: 480,
                  width: "100%",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Green header */}
                <div
                  style={{
                    background: "#059669",
                    color: "#fff",
                    padding: "16px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <h2
                    id="add-recommendation-title"
                    style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#fff" }}
                  >
                    Add Recommendation
                  </h2>
                  <button
                    type="button"
                    onClick={closeAddModal}
                    aria-label="Close"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#fff",
                      fontSize: "20px",
                      cursor: "pointer",
                      padding: "0 4px",
                      lineHeight: 1,
                    }}
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>

                <form onSubmit={handleAddRecommendationSave} style={{ padding: "20px" }}>
                  <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "20px" }}>
                    Please select your scheme and provide a recommendation for it
                  </p>

                  <div className="mb-3">
                    <label className="form-label small fw-semibold text-secondary mb-1 d-block">
                      Select Scheme
                    </label>
                    <select
                      value={modalSchemeId}
                      onChange={(e) => setModalSchemeId(e.target.value)}
                      style={{
                        ...inputStyle,
                        borderColor: "#e2e8f0",
                      }}
                      required
                    >
                      <option value="">Select</option>
                      {SAMPLE_SCHEMES.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.scheme}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-semibold text-secondary mb-2 d-block">
                      Recommendation
                    </label>
                    <div className="d-flex gap-4">
                      {[
                        { value: "buy", label: "Buy" },
                        { value: "sell", label: "Sell" },
                        { value: "hold", label: "Hold" },
                      ].map(({ value, label }) => (
                        <label key={value} className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                          <input
                            type="radio"
                            name="modalRecommendation"
                            value={value}
                            checked={modalRecommendation === value}
                            onChange={() => setModalRecommendation(value)}
                            style={{ accentColor: "#10b981" }}
                            required
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label small fw-semibold text-secondary mb-1 d-block">
                      Remarks (Optional):
                    </label>
                    <textarea
                      value={modalRemarks}
                      onChange={(e) => setModalRemarks(e.target.value.slice(0, REMARKS_MAX))}
                      rows={4}
                      maxLength={REMARKS_MAX}
                      placeholder=""
                      style={{
                        ...inputStyle,
                        borderColor: "#e2e8f0",
                        resize: "vertical",
                      }}
                    />
                    <div className="small text-end mt-1" style={{ color: "#64748b" }}>
                      {remarksLeft} characters left
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      style={{
                        background: "#10b981",
                        color: "#fff",
                        border: "none",
                        padding: "10px 32px",
                        borderRadius: "6px",
                        fontWeight: 600,
                        fontSize: "15px",
                        cursor: "pointer",
                      }}
                    >
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
